// src/pages/BillingHistoryPage.js
import React from 'react';
import { DocumentArrowDownIcon, CalendarDaysIcon, CurrencyDollarIcon, TagIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

// Mock Data for Invoices
const mockInvoices = [
    {
        id: 'inv_001',
        invoiceNumber: 'LL-20250615-001',
        date: new Date('2025-06-15'),
        amount: 99.00,
        status: 'Paid',
        description: 'Monthly Subscription - June 2025',
        downloadLink: '#invoice_001.pdf' // Placeholder link
    },
    {
        id: 'inv_002',
        invoiceNumber: 'LL-20250515-001',
        date: new Date('2025-05-15'),
        amount: 99.00,
        status: 'Paid',
        description: 'Monthly Subscription - May 2025',
        downloadLink: '#invoice_002.pdf'
    },
    {
        id: 'inv_003',
        invoiceNumber: 'LL-20250415-001',
        date: new Date('2025-04-15'),
        amount: 99.00,
        status: 'Overdue',
        description: 'Monthly Subscription - April 2025',
        downloadLink: '#invoice_003.pdf'
    },
    {
        id: 'inv_004',
        invoiceNumber: 'LL-20250315-001',
        date: new Date('2025-03-15'),
        amount: 99.00,
        status: 'Paid',
        description: 'Monthly Subscription - March 2025',
        downloadLink: '#invoice_004.pdf'
    },
    {
        id: 'inv_005',
        invoiceNumber: 'LL-20250215-001',
        date: new Date('2025-02-15'),
        amount: 99.00,
        status: 'Pending',
        description: 'Monthly Subscription - Feb 2025',
        downloadLink: '#invoice_005.pdf'
    },
];

const getStatusClasses = (status) => {
    switch (status) {
        case 'Paid':
            return 'bg-green-100 text-green-800';
        case 'Overdue':
            return 'bg-red-100 text-red-800';
        case 'Pending':
            return 'bg-yellow-100 text-yellow-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

const getStatusIcon = (status) => {
    switch (status) {
        case 'Paid':
            return <CheckCircleIcon className="h-5 w-5 text-green-500 mr-1" />;
        case 'Overdue':
            return <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-1" />;
        case 'Pending':
            return <ClockIcon className="h-5 w-5 text-yellow-500 mr-1" />;
        default:
            return null;
    }
};

const TestPage = () => {
    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h1 className="text-3xl font-extrabold text-gray-900 mb-6 text-center">
                    Billing History
                </h1>
                <p className="text-lg text-gray-600 mb-8 text-center">
                    View your past invoices and payment statuses for your LocalRewards subscription.
                </p>

                {mockInvoices.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        <p className="text-xl">No billing history found.</p>
                        <p className="text-md mt-2">Your invoices will appear here after your first payment.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Invoice
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount (ZAR)
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {mockInvoices.map((invoice) => (
                                <tr key={invoice.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        <div className="flex items-center">
                                            <TagIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            {invoice.invoiceNumber}
                                        </div>
                                        <div className="text-gray-500 text-xs mt-1">{invoice.description}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            {invoice.date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center">
                                            <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                                            R{invoice.amount.toFixed(2)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClasses(invoice.status)}`}
                      >
                        {getStatusIcon(invoice.status)} {invoice.status}
                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <a
                                            href={invoice.downloadLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                            Download
                                        </a>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TestPage;